import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../lib/api";
import { getAuthHeader } from "../lib/auth";

const DEFAULT_TOURNAMENT_ID = "get-active-3v3-75-ovr-2026";

function apiUrl(path) {
  const base = String(api.base || "/api").replace(/\/$/, "");
  const suffix = String(path || "").startsWith("/") ? path : `/${path}`;
  return `${base}${suffix}`;
}

async function requestJSON(path, options = {}) {
  const response = await fetch(apiUrl(path), {
    method: options.method || "GET",
    headers: {
      Accept: "application/json",
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(options.auth ? { Authorization: getAuthHeader() } : {}),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
    mode: "cors",
    credentials: "omit",
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) throw new Error(data?.message || data?.error || `Request failed (${response.status})`);
  return data;
}

function money(cents) {
  if (cents === null || cents === undefined) return "Not checked";
  return `$${(Number(cents || 0) / 100).toFixed(2)}`;
}

function statusLabel(status) {
  const labels = {
    paid: "PAID",
    processing: "PROCESSING",
    waiting_for_team_readiness: "WAITING FOR ALL 3",
    awaiting_platform_funds: "WAITING FOR $300 BALANCE",
    blocked_dispute: "BLOCKED BY DISPUTE",
    stripe_test_mode: "LIVE STRIPE REQUIRED",
    retry_pending: "SAFE RETRY PENDING",
    manual_review_required: "OWNER REVIEW REQUIRED",
    safety_hold: "SAFETY HOLD",
    pending_setup: "SETUP REQUIRED",
    verification_pending: "VERIFICATION PENDING",
  };
  return labels[String(status || "").toLowerCase()] || String(status || "PENDING").replaceAll("_", " ").toUpperCase();
}

export default function TournamentPayoutAdmin() {
  const location = useLocation();
  const tournamentId = useMemo(
    () => new URLSearchParams(location.search).get("tournament") || DEFAULT_TOURNAMENT_ID,
    [location.search]
  );
  const [data, setData] = useState(null);
  const [safety, setSafety] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const dashboard = await requestJSON(
        `/tournaments/${tournamentId}/admin/payout-safety`,
        { auth: true }
      );
      setData(dashboard || null);
      setSafety(dashboard || null);
    } catch (err) {
      setData(null);
      setSafety(null);
      setError(err.message || "Could not load tournament payout safety status.");
    } finally {
      setLoading(false);
    }
  }, [tournamentId]);

  useEffect(() => {
    load();
  }, [load]);

  async function runProtectedBatch() {
    const confirmed = window.confirm(
      "Run the protected winner payout batch? GAStreaming will release exactly three $100 payouts only when all three winners are Stripe-ready, no disputes are open, Stripe is live, and the full $300 is available."
    );
    if (!confirmed) return;

    setBusy(true);
    setError("");
    setMessage("");
    try {
      const result = await requestJSON(`/tournaments/${tournamentId}/admin/payouts/send-batch`, {
        method: "POST",
        auth: true,
      });
      setMessage(result?.message || "Protected three-winner payout batch completed.");
      await load();
    } catch (err) {
      setError(err.message || "The protected payout batch could not run.");
      await load();
    } finally {
      setBusy(false);
    }
  }

  const payouts = Array.isArray(data?.payouts) ? data.payouts : [];
  const snapshot = data?.completionSnapshot || null;
  const paidCount = payouts.filter((payout) => payout.status === "paid").length;
  const allPaid = payouts.length === 3 && paidCount === 3;
  const checks = useMemo(
    () => [
      {
        label: "Winner records",
        value: `${Number(safety?.structural?.winnerCount || 0)}/3`,
        pass: Number(safety?.structural?.winnerCount || 0) === 3,
      },
      {
        label: "Stripe-ready winners",
        value: `${Number(safety?.readyWinnerCount || 0)}/3`,
        pass: Number(safety?.readyWinnerCount || 0) === 3,
      },
      {
        label: "Open disputes",
        value: String(Number(safety?.openDisputeCount || 0)),
        pass: Number(safety?.openDisputeCount || 0) === 0,
      },
      {
        label: "Stripe mode",
        value: safety?.stripeLiveMode === true ? "LIVE" : safety?.stripeLiveMode === false ? "TEST" : "NOT CHECKED",
        pass: safety?.stripeLiveMode === true,
      },
      {
        label: "Available balance",
        value: money(safety?.availableBalanceCents),
        pass: Number(safety?.availableBalanceCents || 0) >= 30000,
      },
    ],
    [safety]
  );

  return (
    <main style={styles.page}>
      <section style={styles.hero}>
        <div style={styles.eyebrow}>TOURNAMENT ADMIN • PAYOUT SAFETY V2</div>
        <h1 style={styles.title}>Protected Winner Payouts</h1>
        <p style={styles.copy}>
          The championship now triggers one protected batch automatically. GAStreaming will never intentionally pay only one or two teammates: the system requires exactly three winners, exactly $100 each, no unresolved disputes, live Stripe mode, and the full $300 available balance.
        </p>
        <div style={styles.heroActions}>
          <button type="button" style={styles.refreshButton} onClick={load} disabled={loading || busy}>
            {loading ? "Checking…" : "Refresh All Safety Checks"}
          </button>
          {!allPaid ? (
            <button
              type="button"
              style={safety?.canSend ? styles.batchButton : styles.disabledBatchButton}
              onClick={runProtectedBatch}
              disabled={busy || loading || !safety?.canSend}
            >
              {busy ? "Running Protected Batch…" : "Send / Retry Protected $300 Batch"}
            </button>
          ) : null}
        </div>
      </section>

      {error ? <div style={styles.error}>{error}</div> : null}
      {message ? <div style={styles.success}>{message}</div> : null}

      <section style={safety?.canSend || allPaid ? styles.safetyReady : styles.safetyHold}>
        <div style={styles.eyebrow}>{allPaid ? "COMPLETE" : safety?.canSend ? "READY TO RELEASE" : "PAYOUT SAFETY HOLD"}</div>
        <div style={styles.safetyMessage}>{safety?.message || "Loading safety checks…"}</div>
        {safety?.safety?.holdMessage ? <div style={styles.holdReason}>{safety.safety.holdMessage}</div> : null}
      </section>

      <section style={styles.checkGrid}>
        {checks.map((check) => (
          <div key={check.label} style={check.pass ? styles.checkPass : styles.checkFail}>
            <span>{check.label}</span>
            <strong>{check.pass ? "✓ " : "✕ "}{check.value}</strong>
          </div>
        ))}
      </section>

      {snapshot ? (
        <section style={styles.championCard}>
          <div style={styles.eyebrow}>CHAMPIONS</div>
          <div style={styles.championName}>{snapshot.teamName}</div>
          <div style={styles.championMeta}>
            Final {snapshot.finalScoreA}–{snapshot.finalScoreB}
            {snapshot.runnerUpTeamName ? ` vs ${snapshot.runnerUpTeamName}` : ""}
          </div>
          <div style={styles.championMeta}>$300.00 total • exactly $100.00 per winner</div>
        </section>
      ) : null}

      <section style={styles.summaryGrid}>
        <div style={styles.summaryCard}><span>Protected total</span><strong>$300.00</strong></div>
        <div style={styles.summaryCard}><span>Already paid</span><strong>{money(data?.paidCents)}</strong></div>
        <div style={styles.summaryCard}><span>Paid players</span><strong>{paidCount}/3</strong></div>
        <div style={styles.summaryCard}><span>Batch status</span><strong>{statusLabel(safety?.safety?.status || (allPaid ? "paid" : "pending"))}</strong></div>
      </section>

      {loading ? (
        <div style={styles.empty}>Loading payout queue…</div>
      ) : payouts.length === 0 ? (
        <div style={styles.empty}>No real winner payout records exist yet. They will be created when the championship result locks.</div>
      ) : (
        <section style={styles.list}>
          {payouts.map((payout) => {
            const paid = payout.status === "paid";
            return (
              <article key={payout.id} style={styles.payoutCard}>
                <div>
                  <div style={styles.playerName}>@{payout.username}</div>
                  <div style={styles.meta}>{payout.teamName}</div>
                  <div style={styles.amount}>$100.00</div>
                </div>
                <div style={styles.statusArea}>
                  <div style={paid ? styles.paidPill : styles.pendingPill}>{statusLabel(payout.status)}</div>
                  {payout.failureMessage ? <div style={styles.failure}>{payout.failureMessage}</div> : null}
                  {Array.isArray(payout.audit) && payout.audit[0]?.message ? <div style={styles.auditLine}>Latest: {payout.audit[0].message}</div> : null}
                  {payout.transferId ? <div style={styles.transferId}>Transfer: {payout.transferId}</div> : null}
                </div>
              </article>
            );
          })}
        </section>
      )}

      <section style={styles.explainer}>
        <strong>How recovery works:</strong> if the server restarts or Stripe returns an uncertain response, GAStreaming checks the batch by its Stripe transfer group and payout IDs before retrying. If only part of a new attempt succeeds, it tries to reverse those transfers; any failed reversal locks the batch for owner review instead of blindly sending more money.
      </section>
    </main>
  );
}

const styles = {
  page: { width: "min(1180px, calc(100% - 28px))", margin: "0 auto 70px", color: "#fff" },
  hero: { padding: "28px 22px", borderRadius: 22, background: "linear-gradient(145deg, rgba(45,8,12,.94), rgba(8,8,12,.95))", border: "1px solid rgba(255,120,120,.2)" },
  eyebrow: { color: "#ff9b9b", fontSize: 12, fontWeight: 1000, letterSpacing: 1.4 },
  title: { margin: "7px 0 8px", fontSize: "clamp(2rem, 6vw, 4.6rem)" },
  copy: { margin: 0, color: "rgba(255,255,255,.75)", lineHeight: 1.58 },
  heroActions: { marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" },
  refreshButton: { border: "1px solid rgba(120,210,255,.35)", borderRadius: 999, background: "rgba(0,120,200,.18)", color: "#c9efff", padding: "11px 16px", fontWeight: 1000, cursor: "pointer" },
  batchButton: { border: "1px solid rgba(90,255,160,.42)", borderRadius: 999, background: "linear-gradient(90deg,#087b45,#0ca960)", color: "#fff", padding: "11px 16px", fontWeight: 1000, cursor: "pointer" },
  disabledBatchButton: { border: "1px solid rgba(255,255,255,.12)", borderRadius: 999, background: "rgba(255,255,255,.06)", color: "rgba(255,255,255,.4)", padding: "11px 16px", fontWeight: 1000, cursor: "not-allowed" },
  error: { marginTop: 14, padding: 13, borderRadius: 14, background: "rgba(130,0,0,.35)", border: "1px solid rgba(255,90,90,.3)", fontWeight: 800 },
  success: { marginTop: 14, padding: 13, borderRadius: 14, background: "rgba(0,100,55,.28)", border: "1px solid rgba(80,255,160,.28)", fontWeight: 800 },
  safetyReady: { marginTop: 16, padding: 18, borderRadius: 18, background: "rgba(0,110,60,.25)", border: "1px solid rgba(80,255,160,.3)" },
  safetyHold: { marginTop: 16, padding: 18, borderRadius: 18, background: "rgba(130,70,0,.25)", border: "1px solid rgba(255,190,70,.32)" },
  safetyMessage: { marginTop: 7, fontSize: 17, fontWeight: 900, lineHeight: 1.5 },
  holdReason: { marginTop: 8, color: "#ffd59a", fontSize: 13, lineHeight: 1.5 },
  checkGrid: { marginTop: 16, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 10 },
  checkPass: { padding: 15, borderRadius: 15, display: "grid", gap: 7, background: "rgba(0,110,60,.22)", border: "1px solid rgba(80,255,160,.24)" },
  checkFail: { padding: 15, borderRadius: 15, display: "grid", gap: 7, background: "rgba(120,45,20,.25)", border: "1px solid rgba(255,130,90,.24)" },
  championCard: { marginTop: 16, padding: 20, borderRadius: 20, textAlign: "center", background: "radial-gradient(circle at 50% 20%, rgba(255,190,45,.18), transparent 50%), rgba(20,10,0,.8)", border: "1px solid rgba(255,215,100,.3)" },
  championName: { marginTop: 6, fontSize: "clamp(2rem, 6vw, 4rem)", fontWeight: 1000, textTransform: "uppercase" },
  championMeta: { marginTop: 7, color: "#ffd76e", fontWeight: 900 },
  summaryGrid: { marginTop: 16, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 },
  summaryCard: { padding: 16, borderRadius: 16, background: "rgba(0,0,0,.44)", border: "1px solid rgba(255,255,255,.1)", display: "grid", gap: 7 },
  list: { marginTop: 16, display: "grid", gap: 12 },
  payoutCard: { padding: 17, borderRadius: 18, background: "linear-gradient(145deg, rgba(35,12,16,.9), rgba(8,8,10,.95))", border: "1px solid rgba(255,255,255,.1)", display: "flex", justifyContent: "space-between", gap: 18, alignItems: "center", flexWrap: "wrap" },
  playerName: { fontWeight: 1000, fontSize: 20 },
  meta: { marginTop: 4, color: "rgba(255,255,255,.6)", fontSize: 13 },
  amount: { marginTop: 8, color: "#ffd666", fontSize: 28, fontWeight: 1000 },
  statusArea: { display: "grid", justifyItems: "end", gap: 8, maxWidth: 500 },
  paidPill: { borderRadius: 999, padding: "7px 10px", background: "rgba(0,150,80,.25)", border: "1px solid rgba(70,255,150,.35)", color: "#75ffb2", fontWeight: 1000, fontSize: 11 },
  pendingPill: { borderRadius: 999, padding: "7px 10px", background: "rgba(150,90,0,.22)", border: "1px solid rgba(255,190,70,.35)", color: "#ffd27c", fontWeight: 1000, fontSize: 11 },
  failure: { color: "#ffb2b2", fontSize: 12, textAlign: "right" },
  auditLine: { color: "rgba(255,255,255,.5)", fontSize: 11, textAlign: "right", maxWidth: 430 },
  transferId: { color: "rgba(255,255,255,.55)", fontSize: 11, wordBreak: "break-all" },
  empty: { marginTop: 16, padding: 20, borderRadius: 16, background: "rgba(0,0,0,.35)", border: "1px dashed rgba(255,255,255,.12)", color: "rgba(255,255,255,.65)" },
  explainer: { marginTop: 16, padding: 18, borderRadius: 16, background: "rgba(0,0,0,.5)", border: "1px solid rgba(255,255,255,.1)", color: "rgba(255,255,255,.72)", lineHeight: 1.55 },
};